// @flow

import React from "react";
import { BaseHierarchyTree, Checkbox, Icons } from "../index";
import type { TreeBranch } from "../base-hierarchy-tree";
import {
  flattenNodes,
  getParentIds,
  TYPE_ORGANIZATION,
  getFirstSelectedParentId,
} from "../base-hierarchy-tree/util";
import withSearchable from "../searchable-hierarchy-tree";
import type { FlatNodes } from "../base-hierarchy-tree/util";
import "./checkbox-hierarchy-tree.style.scss";

type State = $Exact<{
  selected: number[],
  excluded: number[],
}>;

type SelectedItem = $Exact<{
  id: number,
  excluded: number[],
}>;

type Props = $Exact<{
  tree: TreeBranch,
  disabled: boolean,
  selected: number[],
  excluded: number[],
  expanded: number[],
  onChange: (data: Array<SelectedItem>) => void,
}>;

export class CheckboxHierarchyTree extends React.Component<Props, State> {
  static defaultProps = {
    selected: [],
    excluded: [],
    expanded: [],
    onChange: () => undefined,
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (props.selected !== state.selected || props.excluded !== state.excluded) {
      return {
        selected: props.selected,
        excluded: props.excluded,
      };
    }

    return null;
  }

  flatNodes: FlatNodes;
  allSelectedIds: number[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      selected: props.selected,
      excluded: props.excluded,
    };

    this.flatNodes = flattenNodes(props.tree);
  }

  onCheckboxClick = (id: number) => () => {
    // Check if the node has been selected by itself
    const selectedIndex = this.state.selected.indexOf(id);
    let newState: State = { ...this.state };

    if (selectedIndex !== -1) {
      let selected = this.state.selected.filter((_, i) => i !== selectedIndex);
      this.setState({
        selected,
      });

      return this.onChange({
        ...newState,
        selected,
      });
    }

    // Check if the node has been excluded by itself
    const excludedIndex = this.state.excluded.indexOf(id);
    if (this.state.excluded.indexOf(id) !== -1) {
      let excluded = this.state.excluded.filter((_, i) => i !== excludedIndex);
      this.setState({
        excluded,
      });

      return this.onChange({
        ...newState,
        excluded,
      });
    }

    if (this.isSelected(id)) {
      newState = { ...newState, excluded: this.state.excluded.concat([id]) };
    } else {
      newState = { ...newState, selected: this.state.selected.concat([id]) };
    }

    this.setState(newState);
    return this.onChange(newState);
  };

  onChange = (state: State) => {
    let selectedItems: SelectedItem[] = [];

    for (let selectedId of state.selected) {
      let item: SelectedItem = {
        id: selectedId,
        excluded: [],
      };

      for (let excludedId of state.excluded) {
        let parentId = getFirstSelectedParentId(excludedId, state.selected, this.flatNodes);
        if (parentId === selectedId) {
          item.excluded.push(excludedId);
        }
      }

      selectedItems.push(item);
    }

    this.props.onChange(selectedItems);
  };

  get selectedIds(): number[] {
    let selected = [];

    for (let id of this.state.selected) {
      selected = selected.concat(this.filterSelectedNodes(id));
    }

    return Array.from(new Set(selected));
  }

  get expandedNodes() {
    let selected = [];

    for (let id of this.state.selected) {
      selected = selected.concat(this.filterExpandedNodes(id));
    }

    return this.props.expanded.concat(selected);
  }

  filterSelectedNodes = (id: number): number[] => {
    let filteredIds: number[] = [];
    let childIds: number[] = this.flatNodes[id].children;

    if (this.state.excluded.indexOf(id) === -1) {
      filteredIds.push(id);
      childIds.map((childId: number) => {
        filteredIds = filteredIds.concat(this.filterSelectedNodes(childId));
      });
    }

    // Return only unique values
    return Array.from(new Set(filteredIds));
  };

  filterExpandedNodes = (id: number): number[] => {
    let filteredIds: number[] = [];
    let childIds: number[] = this.flatNodes[id].children;

    if (this.state.excluded.indexOf(id) === -1) {
      filteredIds.push(id);
      filteredIds = filteredIds.concat(getParentIds(this.flatNodes, id));
      childIds.map((childId: number) => {
        filteredIds = filteredIds.concat(this.filterExpandedNodes(childId));
      });
    }

    // Return only unique values
    return Array.from(new Set(filteredIds));
  };

  isSelected = (id: number) => {
    return this.allSelectedIds.indexOf(id) !== -1;
  };

  renderControl = (branch: TreeBranch) => {
    if (branch.data.type !== TYPE_ORGANIZATION) {
      return <Icons.PersonIcon className="tree-icon" color="teal" />;
    }

    return (
      <Checkbox
        id={"branch-" + branch.data.path + branch.data.id}
        onChange={this.onCheckboxClick(branch.data.id)}
        checked={this.isSelected(branch.data.id)}
        disabled={branch.data.accessible !== true && branch.data.accessible != null}
      />
    );
  };

  render() {
    this.allSelectedIds = this.selectedIds;

    return (
      <BaseHierarchyTree
        className="checkbox-tree"
        tree={this.props.tree}
        disabled={this.props.disabled}
        expanded={this.expandedNodes}
        hasControl={true}
        renderControl={this.renderControl}
      />
    );
  }
}

export default withSearchable(CheckboxHierarchyTree);
